import React, { ChangeEvent, FocusEvent } from "react";
import styled from "styled-components";

import { FieldOption, resolveOptionLabel } from "./optionLabel";

interface InputProps {
  label?: string;
  /**
   * ラベル右脇の補助テキスト。`"required"` → 「必須」（青）、`"optional"` → 「任意」（薄グレー）。
   * 旧表記（"必須" / "[必須]" / "任意" / "[任意]"）は内部で正規化するが、新規コードでは
   * union 値を渡すこと。
   */
  option?: FieldOption | string;
  type?: 'text' | 'tel' | 'email' | 'number' | 'search' | 'time' | 'password';
  autocomplete?: string;
  placeholder?: string;
  inputmode?: string;
  maxlength?: number;
  width?: string;
  prepend?: string;
  append?: string;
  error?: string;
  name?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** フォーカスを外したときに呼ばれる。インライン保存などに使う。 */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onCompositionStart?: (e: React.CompositionEvent<HTMLInputElement>) => void;
  onCompositionEnd?: (e: React.CompositionEvent<HTMLInputElement>) => void;
  onCompositionUpdate?: (e: React.CompositionEvent<HTMLInputElement>) => void;
}

// styled-components
const StyledIcon = styled.div.attrs({
  className: "material-symbols-rounded",
})`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: none;

  color: var(--soremo-light-gray);

  &:hover {
    color: var(--soremo-light-gray);
  }
`;

const StyledSearchIcon = styled.div.attrs({
  className: "material-symbols-rounded",
})`
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);

  color: var(--soremo-light-gray);

  &:hover {
    color: var(--soremo-light-gray);
  }
`;

const Input: React.FC<InputProps> = ({
  label = "",
  option = "",
  type = "text",
  autocomplete = "",
  placeholder = "",
  inputmode = "",
  maxlength = undefined,
  width = "100%",
  prepend = "",
  append = "",
  error = "",
  name,
  value,
  onChange,
  onBlur,
  onCompositionStart,
  onCompositionEnd,
  onCompositionUpdate,
}) => {
  if (
    ![
      "text",
      "tel",
      "email",
      "number",
      "search",
      "time",
      "password",
    ].includes(type)
  )
    return null;

  const { text: optionLabel, className: optionClass } = resolveOptionLabel(option);
  const containerClass =
    width === "100%" ? "u-col u-w100 u-gap4 u-relative" : "u-col u-fill u-gap4 u-relative";

  return (
    <div className={containerClass}>
      {label && (
        <label className="bodytext-13 u-line-height-100 u-pl4">
          {label}
          {optionLabel && <span className={optionClass}>{optionLabel}</span>}
        </label>
      )}
      <div className="u-row u-w100 u-relative">
        {type === "time" ? (
          <StyledIcon>schedule</StyledIcon>
        ) : type === "search" ? (
          <StyledSearchIcon>search</StyledSearchIcon>
        ) : null}
        {prepend && <span className="u-pr8">{prepend}</span>}

        <input
          type={type}
          placeholder={placeholder}
          autoComplete={autocomplete}
          inputMode={inputmode as any}
          maxLength={maxlength}
          style={{ width }}
          className="u-fill"
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          onCompositionUpdate={onCompositionUpdate}
        />
        {append && <span className="u-pl8">{append}</span>}
      </div>
      {error && (
        <p
          className="bodytext-11 u-text-green u-absolute"
          style={{ bottom: "-20px" }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default Input; 